import React, { memo, useState, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import ModalContainer, { ButtonGroup } from '@/shared/components/modal/ModalContainer';
import StyledPassword from '@/shared/components/atoms/StyledPassword';
import StyledButton from '@/shared/components/atoms/StyledButton';
import { paths } from '@/shared/config/paths';
import theme from '@/styles/theme';
import { useReceptionMode } from '../hooks/useReceptionMode';

type ReceptionExitModalProps = {
	open: boolean;
	onClose: () => void;
};

// 리셉션 모드 이탈 모달 (비밀번호 입력 → exit → 관리자 홈 이동)
const ReceptionExitModal = memo<ReceptionExitModalProps>(({ open, onClose }) => {
	const [password, setPassword] = useState('');
	const [loading, setLoading] = useState(false);
	const { exit } = useReceptionMode();
	const navigate = useNavigate();

	const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value);

	const handleClose = () => {
		setPassword('');
		onClose();
	};

	const handleConfirm = async () => {
		if (!password || loading) return;
		setLoading(true);
		try {
			await exit(password);
			toast.success('리셉션 모드를 종료했습니다.');
			setPassword('');
			navigate(paths.home.path, { replace: true });
		} catch {
		} finally {
			setLoading(false);
		}
	};

	if (!open) return null;

	return (
		<ModalContainer maxWidth={480} title="리셉션 모드 나가기" onClose={handleClose}>
			<Description>
				리셉션 모드에서 나가게 되면 이전에 로그인하셨던 관리자 계정으로 돌아갑니다. 계속하려면 관리자 비밀번호를
				입력하세요.
			</Description>
			<Field>
				<FieldLabel>관리자 비밀번호 입력</FieldLabel>
				<StyledPassword
					value={password}
					onChange={handlePasswordChange}
					onKeyPress={e => e.key === 'Enter' && handleConfirm()}
					placeholder="비밀번호 입력"
				/>
			</Field>
			<ButtonGroup>
				<StyledButton width={130} disabled={loading} onClick={handleClose}>
					취소
				</StyledButton>
				<StyledButton
					width={200}
					fontColor={theme.colors.whiteColor}
					bgColor={theme.colors.activeRedOrange}
					border="none"
					disabled={!password || loading}
					onClick={handleConfirm}>
					리셉션 모드 나가기
				</StyledButton>
			</ButtonGroup>
		</ModalContainer>
	);
});

ReceptionExitModal.displayName = 'ReceptionExitModal';

export default ReceptionExitModal;

const Description = styled.div`
	font-size: 14px;
	line-height: 22px;
	color: ${({ theme }) => theme.colors.deepGrayFontColor};
	margin-bottom: 24px;
`;
const Field = styled.div`
	margin-top: 4px;
`;
const FieldLabel = styled.div`
	font-size: 14px;
	margin-bottom: 8px;
`;
